// Tiny fetch wrapper for the Drop worker API.
// Always sends the session cookie + `origin` header (Better Auth requires it
// for non-GET requests; harmless on GETs).
//
// FRE-272: also forwards the active org id (from localStorage, via
// `getActiveOrgId`) as `X-Active-Org` on every call. Absent header is fine —
// the worker falls back to session.activeOrganizationId / first membership.

import { getActiveOrgId } from "@/lib/activeOrg.ts";

// Centralised header builder so every verb (GET/POST/PATCH/DELETE) attaches
// the same set. Spreading conditionally keeps `X-Active-Org` out of the
// request entirely when no value is known — that path triggers the worker's
// session-active-org / first-org fallback for brand-new sessions.
function buildHeaders(extra?: Record<string, string>): HeadersInit {
  const orgId = getActiveOrgId();
  return {
    origin: window.location.origin,
    ...(orgId ? { "x-active-org": orgId } : {}),
    ...extra,
  };
}

export class UnauthorizedError extends Error {
  readonly status = 401;
  constructor(message = "Unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
  return body?.message ?? body?.error ?? `${res.status} ${res.statusText}`;
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new UnauthorizedError(await parseError(res));
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  return (await res.json()) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: buildHeaders(),
  });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: buildHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    credentials: "include",
    headers: buildHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

// DELETE returns no body on success (204/200) and we treat 404 as
// already-deleted so callers can safely treat it as success (idempotent
// from the UI's perspective — the resource is gone either way).
export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(path, {
    method: "DELETE",
    credentials: "include",
    headers: buildHeaders(),
  });
  if (res.status === 204 || res.status === 200 || res.status === 404) return;
  if (res.status === 401) throw new UnauthorizedError(await parseError(res));
  throw new ApiError(res.status, await parseError(res));
}
